/**
 * Customer JWT session: proactive refresh, single in-flight refresh, 401 retry.
 * Used by team API and any route that needs Bearer auth against the platform API.
 */

import {
  customerRefreshTokens,
  CUSTOMER_ACCESS_TOKEN_KEY,
  CUSTOMER_REFRESH_TOKEN_KEY,
} from './customerAuthApi';
import { apiConnectionHint, getBackendBaseUrl } from '../backendMode';

/** Proactive refresh when access token expires within this many seconds (5 minutes). */
export const ACCESS_TOKEN_REFRESH_SKEW_SECONDS = 300;

/** One silent 401 → refresh → retry per logical request (no loops). */
const MAX_UNAUTHORIZED_RETRIES = 1;

let refreshInFlight: Promise<boolean> | null = null;

function tryRefreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = customerRefreshTokens().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

function decodeJwtExpSeconds(accessToken: string): number | null {
  try {
    const parts = accessToken.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(b64 + pad);
    const payload = JSON.parse(json) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * If access token expires within `skewSeconds` or is already expired, refresh using refresh token first.
 */
export async function ensureAccessTokenFresh(
  skewSeconds: number = ACCESS_TOKEN_REFRESH_SKEW_SECONDS
): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  const access = localStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY);
  const refresh = localStorage.getItem(CUSTOMER_REFRESH_TOKEN_KEY);
  if (!access || !refresh || refresh === 'undefined') return;
  const exp = decodeJwtExpSeconds(access);
  if (exp == null) return;
  const now = Math.floor(Date.now() / 1000);
  if (exp > now + skewSeconds) return;
  const ok = await tryRefreshSession();
  if (ok) {
    const nextExp = decodeJwtExpSeconds(localStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY) || '');
    console.info('[customer-session] proactive refresh ok', {
      access_expires_at: nextExp ? new Date(nextExp * 1000).toISOString() : null,
    });
  } else {
    console.warn('[customer-session] proactive refresh failed (will retry on next 401 if applicable)');
  }
}

async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (e) {
    if (e instanceof TypeError) {
      const browserMsg = e instanceof Error && e.message ? ` ${e.message}` : '';
      throw new Error(apiConnectionHint(getBackendBaseUrl()) + browserMsg);
    }
    throw e;
  }
}

function withAuth(init?: RequestInit): RequestInit {
  const h = new Headers(init?.headers);
  const t = typeof localStorage !== 'undefined' ? localStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY) : null;
  if (t) h.set('Authorization', `Bearer ${t}`);
  const m = (init?.method || 'GET').toUpperCase();
  if (m !== 'GET' && m !== 'HEAD' && init?.body != null && !h.has('Content-Type')) {
    h.set('Content-Type', 'application/json');
  }
  return { ...init, headers: h };
}

/** Same-origin `/api/...` when base is empty; absolute URL when `VITE_BACKEND_API_URL` is set. */
export function buildApiUrl(path: string): string {
  const base = getBackendBaseUrl().replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  if (!base) return p;
  return `${base}${p}`;
}

/**
 * Authorized fetch: proactive refresh, then retry once after refresh on 401 when a refresh token exists.
 */
export async function customerAuthorizedFetch(path: string, init?: RequestInit): Promise<Response> {
  await ensureAccessTokenFresh();
  const fullUrl = buildApiUrl(path);
  let res = await apiFetch(fullUrl, withAuth(init));
  if (res.status !== 401) return res;

  const refresh =
    typeof localStorage !== 'undefined' ? localStorage.getItem(CUSTOMER_REFRESH_TOKEN_KEY) : null;
  const accessBefore = typeof localStorage !== 'undefined' ? localStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY) : null;
  const expBefore = accessBefore ? decodeJwtExpSeconds(accessBefore) : null;
  console.warn('[customer-session] 401 Unauthorized', {
    path,
    method: (init?.method || 'GET').toUpperCase(),
    had_refresh: !!(refresh && refresh !== 'undefined'),
    access_expires_at: expBefore ? new Date(expBefore * 1000).toISOString() : null,
  });

  if (!refresh || refresh === 'undefined') {
    throw new Error(
      'Your session has no refresh token. Please sign out and sign in again so the app can store your refresh token.'
    );
  }

  let attempt = 0;
  while (res.status === 401 && attempt < MAX_UNAUTHORIZED_RETRIES) {
    attempt += 1;
    const refreshed = await tryRefreshSession();
    if (!refreshed) {
      console.error('[customer-session] refresh failed after 401', { path, attempt });
      throw new Error(
        'Session expired and could not be renewed. Please sign out and sign in again. If this persists, confirm the API is deployed with POST /api/auth/customer/refresh and the same CUSTOMER_JWT_SECRET as login.'
      );
    }
    const accessAfter = localStorage.getItem(CUSTOMER_ACCESS_TOKEN_KEY);
    const expAfter = accessAfter ? decodeJwtExpSeconds(accessAfter) : null;
    console.info('[customer-session] silent refresh after 401 ok', {
      path,
      attempt,
      access_expires_at: expAfter ? new Date(expAfter * 1000).toISOString() : null,
    });
    res = await apiFetch(fullUrl, withAuth(init));
  }

  if (res.status === 401) {
    console.error('[customer-session] 401 persists after refresh and retry', { path });
    throw new Error('Session expired. Please sign out and sign in again.');
  }
  return res;
}
